import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Inbox, LogOut, Save, ExternalLink, Loader2, KeyRound } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Logo from "../Logo";
import ContentEditor from "./ContentEditor";
import Messages from "./Messages";
import ChangePassword from "./ChangePassword";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("content");
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => toast.error("Error al cargar contenido"));
  }, []);

  const update = (next) => {
    setContent(next);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/content", content);
      setContent(res.data);
      setDirty(false);
      toast.success("Cambios guardados");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 font-sans text-zinc-900" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-zinc-200 bg-white lg:flex">
        <div className="border-b border-zinc-200 px-6 py-5">
          <Logo height={32} dark />
          <p className="mt-1 text-xs text-zinc-400">Panel de administración</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button onClick={() => setTab("content")} data-testid="tab-content" className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${tab === "content" ? "bg-jauja-olive/10 text-jauja-olive" : "text-zinc-600 hover:bg-zinc-100"}`}>
            <FileText size={18} /> Contenido
          </button>
          <button onClick={() => setTab("messages")} data-testid="tab-messages" className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${tab === "messages" ? "bg-jauja-olive/10 text-jauja-olive" : "text-zinc-600 hover:bg-zinc-100"}`}>
            <Inbox size={18} /> Mensajes
          </button>
          <button onClick={() => setTab("security")} data-testid="tab-security" className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${tab === "security" ? "bg-jauja-olive/10 text-jauja-olive" : "text-zinc-600 hover:bg-zinc-100"}`}>
            <KeyRound size={18} /> Seguridad
          </button>
        </nav>
        <div className="border-t border-zinc-200 p-4">
          <div className="flex items-center gap-3 px-1">
            {user?.picture ? <img src={user.picture} alt="" className="h-8 w-8 rounded-full" /> : <div className="h-8 w-8 rounded-full bg-jauja-olive/20" />}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{user?.name}</p>
              <p className="truncate text-xs text-zinc-400">{user?.email}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="mt-3 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-zinc-500 hover:bg-zinc-100">
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-zinc-200 bg-white/90 px-5 py-4 backdrop-blur">
          <div className="flex items-center gap-2 lg:hidden">
            <button onClick={() => setTab("content")} className={`rounded-lg px-3 py-1.5 text-sm ${tab === "content" ? "bg-jauja-olive/10 text-jauja-olive" : "text-zinc-500"}`}>Contenido</button>
            <button onClick={() => setTab("messages")} className={`rounded-lg px-3 py-1.5 text-sm ${tab === "messages" ? "bg-jauja-olive/10 text-jauja-olive" : "text-zinc-500"}`}>Mensajes</button>
            <button onClick={() => setTab("security")} className={`rounded-lg px-3 py-1.5 text-sm ${tab === "security" ? "bg-jauja-olive/10 text-jauja-olive" : "text-zinc-500"}`}>Seguridad</button>
          </div>
          <h1 className="hidden font-display text-xl lg:block">{tab === "content" ? "Editar contenido" : tab === "messages" ? "Mensajes de contacto" : "Seguridad de la cuenta"}</h1>
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50">
              <ExternalLink size={15} /> Ver sitio
            </a>
            {tab === "content" && (
              <button onClick={save} disabled={saving || !dirty} data-testid="save-content-btn" className="inline-flex items-center gap-2 rounded-lg bg-jauja-olive px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-jauja-olive/90 disabled:opacity-50">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {dirty ? "Guardar cambios" : "Guardado"}
              </button>
            )}
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-5 py-8">
          {tab === "content" ? (
            content ? <ContentEditor content={content} update={update} /> : <p className="text-zinc-500">Cargando…</p>
          ) : tab === "messages" ? (
            <Messages />
          ) : (
            <ChangePassword />
          )}
        </main>
      </div>
    </div>
  );
}
